import React, { useState } from 'react';
import { fn } from '../api/functions';
import { storage } from '../config/firebase';
import { ref, getDownloadURL } from 'firebase/storage';
import jsPDF from 'jspdf';
import QRCode from 'qrcode';

const PDFGenerator = ({ storyId, storyTitle }) => {
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState('');

  const generatePDF = async () => {
    if (!storyId) {
      alert('No story ID provided');
      return;
    }

    setGenerating(true);
    setProgress('Fetching story data...');

    try {
      // Fetch compiled story data from backend
      const result = await fn.compileStoryForPDF({ storyId });
      
      if (!result.data || !result.data.success || !result.data.storyData) {
        throw new Error('Failed to fetch story data from server');
      }
      
      const storyData = result.data.storyData;
      
      if (!storyData || !storyData.questions || storyData.questions.length === 0) {
        throw new Error('No story data found or no questions available');
      }

      setProgress('Creating PDF document...');

      // Create new PDF document (A4 size)
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      // Document settings for professional book layout
      const pageWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const topMargin = 25;
      const bottomMargin = 25;
      const leftMargin = 25;
      const rightMargin = 20;
      const contentWidth = pageWidth - leftMargin - rightMargin;
      const contentHeight = pageHeight - topMargin - bottomMargin;
      let yPosition = topMargin;
      let pageNumber = 1;

      // Professional typography settings
      const titleFontSize = 28;
      const subtitleFontSize = 16;
      const chapterTitleFontSize = 18;
      const chapterNumberFontSize = 14;
      const bodyFontSize = 14; // Increased from 12 for older readers
      const lineSpacing = 1.5;
      const paragraphSpacing = 6;

      // Professional title page
      doc.setFontSize(titleFontSize);
      doc.setFont('times', 'bold');
      
      // Center the title vertically and horizontally
      const titleLines = doc.splitTextToSize(storyTitle || 'My Story', contentWidth * 0.8);
      const titleHeight = titleLines.length * (titleFontSize * 0.35);
      const titleY = pageHeight * 0.35;
      
      titleLines.forEach((line, index) => {
        const textWidth = doc.getTextWidth(line);
        const textX = (pageWidth - textWidth) / 2;
        doc.text(line, textX, titleY + (index * titleFontSize * 0.4));
      });

      // Add subtitle with proper spacing (use story description or fallback)
      doc.setFontSize(subtitleFontSize);
      doc.setFont('times', 'italic');
      const subtitle = storyData.description || storyData.storyTopic || 'A Personal Memoir';
      
      // Handle long descriptions by wrapping them
      const subtitleLines = doc.splitTextToSize(subtitle, contentWidth * 0.9);
      let subtitleY = titleY + titleHeight + 25;
      
      subtitleLines.forEach((line, index) => {
        const lineWidth = doc.getTextWidth(line);
        const lineX = (pageWidth - lineWidth) / 2;
        doc.text(line, lineX, subtitleY + (index * subtitleFontSize * 0.4));
      });

      // Add cover photo if available
      if (storyData.coverPhoto?.url) {
        try {
          // Position cover photo after subtitle
          const coverPhotoY = subtitleY + (subtitleLines.length * subtitleFontSize * 0.4) + 30;
          
          // Load the cover photo
          const response = await fetch(storyData.coverPhoto.url);
          const blob = await response.blob();
          
          // Convert blob to base64 data URL
          const dataUrl = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          });
          
          // Create image element to get dimensions
          const img = new Image();
          await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
            img.src = dataUrl;
          });
          
          // Calculate cover photo dimensions (moderate size for title page)
          const maxCoverWidth = contentWidth * 0.4; // 40% of content width
          const maxCoverHeight = 60; // Maximum height in mm
          
          let coverWidth = maxCoverWidth;
          let coverHeight = (img.height / img.width) * maxCoverWidth;
          
          // If height is too large, scale by height instead
          if (coverHeight > maxCoverHeight) {
            coverHeight = maxCoverHeight;
            coverWidth = (img.width / img.height) * maxCoverHeight;
          }
          
          // Center the cover photo horizontally
          const coverX = leftMargin + (contentWidth - coverWidth) / 2;
          
          // Add the cover photo to PDF
          doc.addImage(dataUrl, 'JPEG', coverX, coverPhotoY, coverWidth, coverHeight);
        } catch (coverPhotoError) {
          console.error('Error adding cover photo to PDF:', coverPhotoError);
        }
      }

      // Add author or date at bottom of title page
      doc.setFontSize(12);
      doc.setFont('times', 'normal');
      const currentYear = new Date().getFullYear();
      const authorLine = `${currentYear}`;
      const authorWidth = doc.getTextWidth(authorLine);
      const authorX = (pageWidth - authorWidth) / 2;
      doc.text(authorLine, authorX, pageHeight - 40);

      // Helper function to add page numbers and headers
      const addPageNumberAndHeader = (pageNum) => {
        doc.setFontSize(10);
        doc.setFont('times', 'normal');
        
        // Page number at bottom center
        const pageText = pageNum.toString();
        const pageWidth_local = doc.getTextWidth(pageText);
        doc.text(pageText, (pageWidth - pageWidth_local) / 2, pageHeight - 15);
        
        // Optional header with story title (on pages after title page)
        if (pageNum > 1) {
          doc.setFontSize(9);
          doc.setFont('times', 'italic');
          const headerText = (storyTitle || 'My Story').toUpperCase();
          const headerWidth = doc.getTextWidth(headerText);
          doc.text(headerText, (pageWidth - headerWidth) / 2, 15);
        }
      };

      // Helper function to ensure consistent body text formatting
      const setBodyTextFormat = () => {
        doc.setFont('times', 'normal');
        doc.setFontSize(bodyFontSize);
      };

      // Table of Contents page
      doc.addPage();
      pageNumber++;
      addPageNumberAndHeader(pageNumber);
      yPosition = topMargin + 20;

      // Table of Contents title
      doc.setFontSize(20);
      doc.setFont('times', 'bold');
      const tocTitle = 'TABLE OF CONTENTS';
      const tocTitleWidth = doc.getTextWidth(tocTitle);
      doc.text(tocTitle, (pageWidth - tocTitleWidth) / 2, yPosition);
      yPosition += 30;

      // TOC entries
      setBodyTextFormat();
      
      let currentPage = pageNumber + 1; // Start counting from next page
      
      for (let i = 0; i < storyData.questions.length; i++) {
        const question = storyData.questions[i];
        
        // Check if we need a new page for TOC
        if (yPosition > pageHeight - bottomMargin - 20) {
          doc.addPage();
          pageNumber++;
          addPageNumberAndHeader(pageNumber);
          yPosition = topMargin + 10;
          setBodyTextFormat(); // Ensure consistent font on new TOC page
        }

        // Clean up question text for TOC
        let tocEntry = question.text.replace(/["""]/g, '').trim();
        if (tocEntry.endsWith('?')) {
          tocEntry = tocEntry.slice(0, -1);
        }
        
        // Truncate long titles for TOC
        if (tocEntry.length > 65) {
          tocEntry = tocEntry.substring(0, 62) + '...';
        }

        // Chapter entry with page number
        const chapterLine = `Chapter ${i + 1}: ${tocEntry}`;
        const pageNumStr = currentPage.toString();
        
        // Draw dotted line between title and page number (only if space available)
        const textWidth = doc.getTextWidth(chapterLine);
        const pageNumWidth = doc.getTextWidth(pageNumStr);
        const availableSpace = contentWidth - textWidth - pageNumWidth - 5;
        
        // Only add dots if there's enough space
        const dotCount = Math.max(0, Math.floor(availableSpace / doc.getTextWidth('.')));
        const dots = dotCount > 0 ? '.'.repeat(dotCount) : '';
        
        doc.text(chapterLine, leftMargin, yPosition);
        if (dots) {
          doc.text(dots, leftMargin + textWidth + 2, yPosition);
        }
        doc.text(pageNumStr, pageWidth - rightMargin - pageNumWidth, yPosition);
        
        yPosition += 14;
        currentPage++; // Each chapter gets its own page
      }

      // Start content on new page
      doc.addPage();
      pageNumber++;
      addPageNumberAndHeader(pageNumber);
      yPosition = topMargin + 10;

      setProgress('Generating QR codes...');

      // Pre-generate all QR codes for audio URLs
      const qrCodes = new Map();
      for (const question of storyData.questions) {
        if (question.answers) {
          for (const answer of question.answers) {
            // Try multiple possible audio URL property names
            let audioUrl = answer.audioUrl || answer.audioFile || answer.audioPath || answer.downloadUrl;
            
            // If no direct URL found, construct Firebase Storage URL from answer ID
            if (!audioUrl && answer.id) {
              audioUrl = `https://firebasestorage.googleapis.com/v0/b/memory-stream-12e9d.appspot.com/o/audio%2F${answer.id}.webm?alt=media`;
            }
            
            if (audioUrl && !qrCodes.has(audioUrl)) {
              try {
                const qrCodeData = await QRCode.toDataURL(audioUrl, {
                  width: 60,
                  margin: 1
                });
                qrCodes.set(audioUrl, qrCodeData);
              } catch (qrError) {
                console.error('Error generating QR code:', qrError);
              }
            }
          }
        }
      }

      setProgress('Processing chapters...');

      // Process each question as a chapter with professional formatting
      for (let i = 0; i < storyData.questions.length; i++) {
        const question = storyData.questions[i];
        setProgress(`Processing chapter ${i + 1} of ${storyData.questions.length}...`);

        // Start each chapter on a new page (except first)
        if (i > 0) {
          doc.addPage();
          pageNumber++;
          
          // Ensure chapters start on odd pages (right-hand pages)
          // Page 1 is odd, so even page numbers need a blank page added
          if (pageNumber % 2 === 0) {
            addPageNumberAndHeader(pageNumber);
            doc.addPage();
            pageNumber++;
          }
          
          addPageNumberAndHeader(pageNumber);
          yPosition = topMargin + 30; // Increased top margin for better chapter opening
        } else {
          // First chapter - increase top margin
          yPosition = topMargin + 30;
        }

        // Chapter number (small, centered)
        doc.setFontSize(chapterNumberFontSize);
        doc.setFont('times', 'normal');
        const chapterNumber = `CHAPTER ${i + 1}`;
        const chapterNumWidth = doc.getTextWidth(chapterNumber);
        doc.text(chapterNumber, (pageWidth - chapterNumWidth) / 2, yPosition);
        yPosition += 20;

        // Chapter title (question as title, centered, larger)
        doc.setFontSize(chapterTitleFontSize);
        doc.setFont('times', 'bold');
        
        // Clean up the question text for use as chapter title
        let chapterTitle = question.text.replace(/["""]/g, '').trim();
        if (chapterTitle.endsWith('?')) {
          chapterTitle = chapterTitle.slice(0, -1);
        }
        
        const titleLines = doc.splitTextToSize(chapterTitle, contentWidth * 0.8);
        titleLines.forEach(line => {
          const lineWidth = doc.getTextWidth(line);
          doc.text(line, (pageWidth - lineWidth) / 2, yPosition);
          yPosition += chapterTitleFontSize * 0.4;
        });

        yPosition += 8; // Space before decorative line
        
        // Add decorative separator line
        const lineWidth = 40; // 40mm centered line
        const lineX = (pageWidth - lineWidth) / 2;
        doc.setLineWidth(0.5);
        doc.line(lineX, yPosition, lineX + lineWidth, yPosition);
        
        yPosition += 20; // Increased space after decorative line before content

        // Chapter content (answers formatted as paragraphs)
        if (question.answers && question.answers.length > 0) {
          setBodyTextFormat(); // Ensure consistent formatting
          
          for (const [answerIndex, answer] of question.answers.entries()) {
            // Try multiple possible audio URL property names
            let audioUrl = answer.audioUrl || answer.audioFile || answer.audioPath || answer.downloadUrl;
            
            // If no direct URL found, construct Firebase Storage URL from answer ID
            if (!audioUrl && answer.id) {
              audioUrl = `https://firebasestorage.googleapis.com/v0/b/memory-stream-12e9d.appspot.com/o/audio%2F${answer.id}.webm?alt=media`;
            }
            
            // Add QR code for audio if available (before answer text)
            if (audioUrl && qrCodes.has(audioUrl)) {
              try {
                if (yPosition > pageHeight - bottomMargin - 55) {
                  doc.addPage();
                  pageNumber++;
                  addPageNumberAndHeader(pageNumber);
                  yPosition = topMargin + 10;
                }
                
                const qrCodeData = qrCodes.get(audioUrl);
                const qrSize = 32; // Professional size for A4 printing
                
                // Center the QR code on the page (not just content area)
                const qrXPosition = (pageWidth - qrSize) / 2;
                doc.addImage(qrCodeData, 'PNG', qrXPosition, yPosition, qrSize, qrSize);
                
                // Position text centered below QR code
                const textYPosition = yPosition + qrSize + 3;
                doc.setFontSize(9);
                doc.setFont('times', 'italic');
                
                // Center the text on the page
                const audioText = 'Scan to hear audio recording';
                const textWidth = doc.getTextWidth(audioText);
                const textXPosition = (pageWidth - textWidth) / 2;
                
                doc.text(audioText, textXPosition, textYPosition);
                setBodyTextFormat(); // Reset to body text format
                
                yPosition = textYPosition + 18; // Increased spacing after QR section before text starts
              } catch (qrError) {
                console.error('Error adding QR code to PDF:', qrError);
              }
            }

            // Try multiple possible text properties
            const answerText = answer.text || answer.cleanedTranscription || answer.transcription;
            
            if (answerText && answerText.trim()) {
              // Split into paragraphs and format professionally
              const paragraphs = answerText.split(/\n\s*\n/).filter(p => p.trim());
              
              paragraphs.forEach((paragraph, paragraphIndex) => {
                // Check if we need a new page
                const estimatedLines = Math.ceil(paragraph.length / 80); // Rough estimate
                const neededSpace = estimatedLines * bodyFontSize * lineSpacing * 0.35 + paragraphSpacing;
                
                if (yPosition + neededSpace > pageHeight - bottomMargin) {
                  doc.addPage();
                  pageNumber++;
                  addPageNumberAndHeader(pageNumber);
                  yPosition = topMargin + 10;
                  // Reset body text formatting after page break
                  setBodyTextFormat();
                }

                // First paragraph of first answer in chapter gets drop cap
                const isFirstParagraph = (answerIndex === 0 && paragraphIndex === 0);
                
                if (isFirstParagraph && paragraph.length > 0) {
                  // Draw drop cap aligned with first line baseline
                  const dropCapLetter = paragraph.charAt(0).toUpperCase();
                  const restOfParagraph = paragraph.slice(1).trim();
                  
                  // Draw the drop cap at the same baseline as first line
                  doc.setFontSize(42);
                  doc.setFont('times', 'bold');
                  doc.text(dropCapLetter, leftMargin, yPosition);
                  const dropCapWidth = doc.getTextWidth(dropCapLetter) + 2;
                  
                  // Format first line next to drop cap, then continue normally
                  setBodyTextFormat();
                  const allLines = doc.splitTextToSize(restOfParagraph, contentWidth);
                  
                  // First line next to drop cap
                  if (allLines.length > 0) {
                    const firstLineWidth = contentWidth - dropCapWidth - 2;
                    const firstLineWords = restOfParagraph.split(' ');
                    let firstLine = '';
                    let remainingText = restOfParagraph;
                    
                    // Build first line to fit next to drop cap
                    for (let i = 0; i < firstLineWords.length; i++) {
                      const testLine = firstLine + (firstLine ? ' ' : '') + firstLineWords[i];
                      if (doc.getTextWidth(testLine) > firstLineWidth) {
                        break;
                      }
                      firstLine = testLine;
                    }
                    
                    // Draw first line next to drop cap
                    doc.text(firstLine, leftMargin + dropCapWidth + 2, yPosition);
                    yPosition += bodyFontSize * lineSpacing * 0.35;
                    
                    // Get remaining text after first line
                    remainingText = restOfParagraph.substring(firstLine.length).trim();
                    
                    // Draw remaining lines at full width
                    if (remainingText) {
                      const remainingLines = doc.splitTextToSize(remainingText, contentWidth);
                      remainingLines.forEach(line => {
                        doc.text(line, leftMargin, yPosition);
                        yPosition += bodyFontSize * lineSpacing * 0.35;
                      });
                    }
                  }
                  
                  yPosition += paragraphSpacing;
                } else {
                  // Normal paragraph formatting (no drop cap)
                  // First line indent for paragraphs (except first paragraph of chapter)
                  const indent = (answerIndex > 0 || paragraphIndex > 0) ? 5 : 0;
                  
                  // Ensure consistent formatting before processing paragraph
                  setBodyTextFormat();
                  const paragraphLines = doc.splitTextToSize(paragraph.trim(), contentWidth - indent);
                  
                  paragraphLines.forEach((line, lineIndex) => {
                    // Check if we need a new page for this line
                    if (yPosition + (bodyFontSize * lineSpacing * 0.35) > pageHeight - bottomMargin) {
                      doc.addPage();
                      pageNumber++;
                      addPageNumberAndHeader(pageNumber);
                      yPosition = topMargin + 10;
                      // Reset body text formatting after page break
                      setBodyTextFormat();
                    }
                    
                    const xPosition = leftMargin + (lineIndex === 0 ? indent : 0);
                    doc.text(line, xPosition, yPosition);
                    yPosition += bodyFontSize * lineSpacing * 0.35;
                  });

                  yPosition += paragraphSpacing; // Space between paragraphs
                }
              });
            }

            // Add photos for this answer (after the answer text)
            const photosToRender = answer.photos || [];
            
            if (photosToRender && photosToRender.length > 0) {
              yPosition += 10; // Space before photos section
              
              for (const photo of photosToRender) {
                try {
                  let dataUrl, imgWidth, imgHeight;
                  
                  // Handle data URLs directly (test photos)
                  if (photo.url.startsWith('data:')) {
                    dataUrl = photo.url;
                    // Get dimensions from data URL
                    const tempImg = new Image();
                    await new Promise((resolve, reject) => {
                      tempImg.onload = () => {
                        imgWidth = tempImg.width;
                        imgHeight = tempImg.height;
                        resolve();
                      };
                      tempImg.onerror = reject;
                      tempImg.src = dataUrl;
                    });
                  } else {
                    // Load Firebase Storage image (CORS configured for localhost)
                    const img = new Image();
                    img.crossOrigin = 'anonymous';
                    
                    await new Promise((resolve, reject) => {
                      img.onload = () => {
                        imgWidth = img.width;
                        imgHeight = img.height;
                        resolve();
                      };
                      img.onerror = (error) => {
                        console.error('Image load error:', error);
                        reject(error);
                      };
                      img.src = photo.url;
                    });
                    
                    // Convert to canvas and then to base64
                    const canvas = document.createElement('canvas');
                    canvas.width = imgWidth;
                    canvas.height = imgHeight;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0);
                    dataUrl = canvas.toDataURL('image/jpeg', 0.8);
                  }
                  
                  // Calculate photo dimensions - 90% of content width for better proportion
                  const maxWidth = contentWidth * 0.9;
                  const maxHeight = 180; // Much taller to fit 2 portrait photos per page
                  
                  let photoWidth = maxWidth;
                  let photoHeight = (imgHeight / imgWidth) * maxWidth;
                  
                  // If height is too large, scale by height instead
                  if (photoHeight > maxHeight) {
                    photoHeight = maxHeight;
                    photoWidth = (imgWidth / imgHeight) * maxHeight;
                  }
                  
                  // Check if we need a new page for the photo
                  if (yPosition + photoHeight + 30 > pageHeight - bottomMargin) {
                    doc.addPage();
                    pageNumber++;
                    addPageNumberAndHeader(pageNumber);
                    yPosition = topMargin + 10;
                  }
                  
                  // Center photos horizontally
                  const photoX = leftMargin + (contentWidth - photoWidth) / 2;
                  
                  // Add the actual image to PDF
                  doc.addImage(dataUrl, 'JPEG', photoX, yPosition, photoWidth, photoHeight);
                  yPosition += photoHeight + 5;
                  
                  // Add photo caption ONLY if user provided one (no filename fallback)
                  if (photo.caption && photo.caption.trim()) {
                    doc.setFontSize(bodyFontSize); // Same size as body text
                    doc.setFont('times', 'italic');
                    
                    const captionLines = doc.splitTextToSize(photo.caption.trim(), contentWidth * 0.8);
                    
                    captionLines.forEach(line => {
                      const captionWidth = doc.getTextWidth(line);
                      const captionX = leftMargin + (contentWidth - captionWidth) / 2;
                      doc.text(line, captionX, yPosition);
                      yPosition += bodyFontSize * 0.4; // Proportional line height
                    });
                    
                    setBodyTextFormat(); // Reset to body text format
                    yPosition += 10; // Extra space after caption
                  }
                  
                  yPosition += 10; // Space after photo (reduced since photos are larger now)
                  
                } catch (photoError) {
                  console.error('Error loading photo:', photoError);
                  
                  // Fallback to placeholder if image fails to load
                  doc.setFontSize(10);
                  doc.setFont('times', 'italic');
                  
                  const photoPlaceholder = `[Photograph: ${photo.fileName || 'Memory Image'} - Unable to load]`;
                  const placeholderWidth = doc.getTextWidth(photoPlaceholder);
                  const placeholderX = leftMargin + (contentWidth - placeholderWidth) / 2;
                  
                  doc.text(photoPlaceholder, placeholderX, yPosition);
                  yPosition += 15;
                  
                  setBodyTextFormat(); // Reset to body text format
                }
              }
            }
          }
        }

        // Remove the old question-level photo code that was in the wrong place
      }

      setProgress('Finalizing PDF...');

      // Save the PDF
      const fileName = `${storyTitle || 'My_Story'}_Memoir.pdf`.replace(/[^a-zA-Z0-9_-]/g, '_');
      doc.save(fileName);

      setProgress('PDF generated successfully!');
      
      setTimeout(() => {
        setProgress('');
        setGenerating(false);
      }, 2000);

    } catch (error) {
      console.error('Error generating PDF:', error);
      alert(`Error generating PDF: ${error.message}`);
      setGenerating(false);
      setProgress('');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Generate a draft version of your memoir to review before publishing. This PDF includes your story content, photos with captions, and QR codes for audio playback.
        </p>
        
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-semibold text-gray-900 mb-3">Your draft PDF will include:</h4>
          <ul className="text-sm text-gray-700 space-y-2">
            <li>• QR codes linking to audio recordings</li>
            <li>• Photos with captions at the end of each chapter</li>
            <li>• 6×9 inch format (same size as your published book)</li>
            <li>• Professional memoir-style formatting</li>
          </ul>
          <p className="text-xs text-gray-600 mt-3 italic">
            Perfect for reviewing your content before ordering the published version.
          </p>
        </div>

        {progress && (
          <div className="bg-blue-50 border border-blue-200 p-3 rounded">
            <p className="text-sm text-blue-700">{progress}</p>
          </div>
        )}

        <button
          onClick={generatePDF}
          disabled={generating}
          className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium py-3 px-4 rounded-lg transition-colors text-base"
        >
          {generating ? 'Generating Draft PDF...' : 'Generate Draft PDF'}
        </button>
      </div>
    </div>
  );
};

export default PDFGenerator;
